import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  ExternalLink,
  Calendar,
  MapPin,
  DollarSign,
  Phone,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Trash2,
  Archive,
  Search,
  Filter,
  SortAsc,
  SortDesc
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8787' : 'https://slack-ai-worker.zlceapps.workers.dev');

const SavedRecommendations = ({ userId, onSaveRecommendation, onUnsaveRecommendation }) => {
  const [savedRecommendations, setSavedRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRecommendations, setExpandedRecommendations] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');

  useEffect(() => {
    if (userId) {
      fetchSavedRecommendations();
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh saved recommendations when component becomes visible (like monitored channels)
  useEffect(() => {
    // This will run every time the component mounts/becomes active
    if (userId) {
      console.log('📋 SavedRecommendations component activated - refreshing data');
      fetchSavedRecommendations();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose refresh function to parent
  useEffect(() => {
    if (onSaveRecommendation) {
      onSaveRecommendation(() => fetchSavedRecommendations());
    }
  }, [onSaveRecommendation]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSavedRecommendations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/saved-recommendations/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        setSavedRecommendations(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch saved recommendations');
        setSavedRecommendations([]); // Set empty array on error
      }
    } catch (error) {
      console.error('Error fetching saved recommendations:', error);
      setSavedRecommendations([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };


  const unsaveRecommendation = async (savedId) => {
    try {
      // Find the recommendation before removing it
      const savedRec = savedRecommendations.find(item => item.id === savedId);
      
      const response = await fetch(`${API_BASE}/api/saved-recommendations/${savedId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });

      if (response.ok) {
        toast.success('Recommendation removed from saved');
        setSavedRecommendations(prev => prev.filter(item => item.id !== savedId));
        
        // Notify parent component about the unsave action
        if (onUnsaveRecommendation && savedRec) {
          onUnsaveRecommendation(savedRec.recommendation_id);
        }
      } else {
        toast.error('Failed to remove saved recommendation');
      }
    } catch (error) {
      console.error('Error removing saved recommendation:', error);
      toast.error('Failed to remove saved recommendation');
    }
  };

  const clearAllSavedRecommendations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/saved-recommendations/${userId}/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Cleared ${result.deletedCount} saved recommendations`);
        setSavedRecommendations([]);
        
        // Notify parent component to refresh saved IDs
        if (onUnsaveRecommendation) {
          // Clear the saved IDs in the parent component
          window.location.reload(); // Simple way to refresh everything
        }
      } else {
        const errorData = await response.json();
        toast.error(`Failed to clear saved recommendations: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error clearing saved recommendations:', error);
      toast.error('Failed to clear saved recommendations');
    }
  };

  const toggleRecommendationExpansion = (recId) => {
    const newExpanded = new Set(expandedRecommendations);
    if (newExpanded.has(recId)) {
      newExpanded.delete(recId);
    } else {
      newExpanded.add(recId);
    }
    setExpandedRecommendations(newExpanded);
  };

  const openInSlack = (channelId, messageTs) => {
    const slackUrl = `slack://channel?team=YOUR_TEAM_ID&id=${channelId}&message=${messageTs}`;
    window.open(slackUrl, '_blank');
  };

  const cleanText = (text) => {
    if (!text) return text;
    
    return text
      .replace(/:[a-zA-Z0-9_+-]+:/g, '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`[^`]*`/g, '')
      .replace(/[!]{2,}/g, '!')
      .replace(/[?]{2,}/g, '?')
      .replace(/[.]{3,}/g, '...')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseMessageData = (messageText) => {
    try {
      const parsed = JSON.parse(messageText);
      return {
        originalText: cleanText(parsed.originalText || messageText),
        title: cleanText(parsed.title || parsed.summary || 'Slack Message'),
        snippet: cleanText(parsed.snippet || parsed.summary || messageText.substring(0, 150) + '...'),
        extractedInfo: parsed.extractedInfo || {}
      };
    } catch (e) {
      const cleanedText = cleanText(messageText);
      const words = cleanedText.split(' ').slice(0, 8).join(' ');
      const title = words.length > 50 ? words.substring(0, 50) + '...' : words || 'Slack Message';
      
      return {
        originalText: cleanedText,
        title: title,
        snippet: cleanedText.substring(0, 150) + '...',
        extractedInfo: {}
      };
    }
  };

  const renderExtractedInfo = (extractedInfo) => {
    const items = [];
    
    if (extractedInfo.eventDate || extractedInfo.eventTime) {
      let dateTimeValue = '';
      if (extractedInfo.eventDate && extractedInfo.eventTime) {
        dateTimeValue = `${extractedInfo.eventDate}, ${extractedInfo.eventTime}`;
      } else if (extractedInfo.eventDate) {
        dateTimeValue = extractedInfo.eventDate;
      } else if (extractedInfo.eventTime) {
        dateTimeValue = extractedInfo.eventTime;
      }
      
      items.push({
        icon: Calendar,
        label: 'Date & Time',
        value: dateTimeValue,
        color: 'text-blue-600'
      });
    }
    
    if (extractedInfo.location) {
      items.push({
        icon: MapPin,
        label: 'Location',
        value: extractedInfo.location,
        color: 'text-green-600'
      });
    }
    
    if (extractedInfo.price) {
      items.push({
        icon: DollarSign,
        label: 'Price',
        value: extractedInfo.price,
        color: 'text-purple-600'
      });
    }
    
    if (extractedInfo.contact) {
      items.push({
        icon: Phone,
        label: 'Contact',
        value: extractedInfo.contact,
        color: 'text-orange-600'
      });
    }
    
    if (extractedInfo.deadline) {
      items.push({
        icon: AlertTriangle,
        label: 'Deadline',
        value: extractedInfo.deadline,
        color: 'text-red-600'
      });
    }
    
    if (extractedInfo.links && extractedInfo.links.length > 0) {
      extractedInfo.links.forEach((link, index) => {
        items.push({
          icon: ExternalLink,
          label: link.label || 'Link',
          value: link.url,
          color: 'text-indigo-600',
          isLink: true
        });
      });
    }
    
    return items;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-600 bg-green-50';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  // Filter and sort recommendations - ensure savedRecommendations is always an array
  const filteredAndSortedRecommendations = (Array.isArray(savedRecommendations) ? savedRecommendations : [])
    .filter(item => {
      const messageData = parseMessageData(item.message_text);
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = !searchTerm || 
        messageData.title.toLowerCase().includes(searchLower) ||
        messageData.snippet.toLowerCase().includes(searchLower) ||
        item.preference_keyword.toLowerCase().includes(searchLower) ||
        item.channel_name.toLowerCase().includes(searchLower);

      const matchesFilter = filterBy === 'all' || 
        (filterBy === 'high-confidence' && item.confidence >= 90) ||
        (filterBy === 'recent' && new Date(item.saved_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.saved_at);
          bValue = new Date(b.saved_at);
          break;
        case 'confidence':
          aValue = a.confidence;
          bValue = b.confidence;
          break;
        case 'preference':
          aValue = a.preference_keyword.toLowerCase();
          bValue = b.preference_keyword.toLowerCase();
          break;
        default:
          aValue = new Date(a.saved_at);
          bValue = new Date(b.saved_at);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading saved recommendations...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Saved Recommendations ({savedRecommendations.length})</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {savedRecommendations.length} saved items
          </div>
          {savedRecommendations.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm(`Are you sure you want to clear all ${savedRecommendations.length} saved recommendations? This action cannot be undone.`)) {
                  clearAllSavedRecommendations();
                }
              }}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2 font-medium shadow-sm transition-all duration-200"
              title="Clear all saved recommendations"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear All Saved</span>
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search saved items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm appearance-none"
            >
              <option value="all">All Items</option>
              <option value="high-confidence">High Confidence (90%+)</option>
              <option value="recent">Saved This Week</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            >
              <option value="date">Sort by Date Saved</option>
              <option value="confidence">Sort by Confidence</option>
              <option value="preference">Sort by Preference</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
            >
              {sortOrder === 'asc' ? (
                <>
                  <SortAsc className="w-4 h-4 mr-2" />
                  Ascending
                </>
              ) : (
                <>
                  <SortDesc className="w-4 h-4 mr-2" />
                  Descending
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredAndSortedRecommendations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Archive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          {savedRecommendations.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No saved recommendations yet</h3>
              <p className="text-gray-600">Start saving recommendations from your dashboard to build your collection.</p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No items match your search</h3>
              <p className="text-gray-600">Try adjusting your search terms or filters.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAndSortedRecommendations.map((item) => {
            const messageData = parseMessageData(item.message_text);
            const extractedItems = renderExtractedInfo(messageData.extractedInfo);
            const isExpanded = expandedRecommendations.has(item.id);
            
            return (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                      {item.preference_keyword}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(item.confidence)}`}>
                      {Math.round(item.confidence)}% match
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs font-medium">
                      Saved
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>#{item.channel_name}</span>
                    <span>•</span>
                    <span>@{item.user_name}</span>
                    <span>•</span>
                    <span>Saved {new Date(item.saved_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Title and Snippet */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {messageData.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {messageData.snippet}
                  </p>
                </div>
                
                {/* Extracted Information */}
                {extractedItems.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <div className="grid grid-cols-2 gap-3">
                      {extractedItems.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <item.icon className={`w-4 h-4 ${item.color}`} />
                          <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                          {item.isLink ? (
                            <a 
                              href={item.value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-indigo-600 hover:text-indigo-800 underline break-all"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-900">{item.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expandable explanation */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleRecommendationExpansion(item.id)}
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    <span>Why this was flagged</span>
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>AI Reasoning:</strong> {item.reasoning}
                      </p>
                      <details className="text-sm text-gray-600">
                        <summary className="cursor-pointer hover:text-gray-800 font-medium">
                          View original message
                        </summary>
                        <div className="mt-2 p-2 bg-white rounded border text-gray-800">
                          {messageData.originalText}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
                
                {/* Action buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => openInSlack(item.channel_id, item.slack_message_ts)}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Open in Slack</span>
                  </button>
                  <button
                    onClick={() => unsaveRecommendation(item.id)}
                    className="px-3 py-1 border border-red-300 text-red-700 text-sm rounded-md hover:bg-red-50 transition-colors flex items-center space-x-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Remove from Saved</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedRecommendations;